import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import landingApiService from '../../../../services/landingApi';
import { sortMediaKits } from '../../../../utils/mediaKitDisplay';
import MediaKitCard from './MediaKitCard';

const MediaKitsTeaser = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const [programKits, setProgramKits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isMobile) {
      setProgramKits([]);
      setLoading(false);
      return undefined;
    }

    const pickRandomKits = (kits, count = 6) => {
      const shuffled = [...kits].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count);
    };

    let cancelled = false;
    landingApiService.getProgramMediaKits().then((data) => {
      if (cancelled) return;
      const sortedKits = sortMediaKits(Array.isArray(data) ? data : []);
      setProgramKits(pickRandomKits(sortedKits, 6));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isMobile]);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        maxWidth: '100%',
        borderRadius: { xs: 3, md: 5 },
        mb: 5,
        background: `linear-gradient(145deg, ${alpha(secondary, 0.1)} 0%, ${alpha(primary, 0.06)} 42%, ${alpha(
          secondary,
          0.04,
        )} 100%)`,
        border: `1px solid ${alpha(primary, 0.16)}`,
        boxShadow: `0 20px 48px ${alpha(primary, 0.1)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 240,
          height: 240,
          borderRadius: '50%',
          top: -80,
          right: -40,
          background: `radial-gradient(circle, ${alpha(secondary, 0.2)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Grid container>
        <Grid item xs={12} lg={4}>
          <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 2.5, color: secondary, mb: 1.5, display: 'block' }}
            >
              Biblioteca comercial
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                fontSize: { xs: '1.85rem', sm: '2.5rem', md: '2.75rem' },
                color: primary,
                mb: 2,
              }}
            >
              Media Kits
              <Box
                component="span"
                sx={{
                  display: 'block',
                  background: `linear-gradient(90deg, ${primary} 0%, ${secondary} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                de programas
              </Box>
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '1.02rem', lineHeight: 1.65, mb: 2.5, flex: 1 }}>
              Propuestas listas por show: audiencia, formatos, precios referenciales y CTA comercial. Explora la biblioteca
              completa de programas de la parrilla TNO Network.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button
                component={RouterLink}
                to="/media-kits"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<FolderOpenIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: 999,
                  px: 3.5,
                  py: 1.35,
                  fontWeight: 800,
                  boxShadow: `0 10px 28px ${alpha(primary, 0.35)}`,
                }}
              >
                Ir a biblioteca
              </Button>
            </Box>
          </Box>
        </Grid>

        {!isMobile && (
          <Grid item xs={12} lg={8}>
            <Box sx={{ p: { xs: 2.5, sm: 3, md: 4, lg: 3 }, maxWidth: '100%', boxSizing: 'border-box' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
                  <CircularProgress size={36} />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {programKits.map((kit) => (
                    <Grid item xs={12} sm={6} md={4} key={kit._id || kit.slug}>
                      <MediaKitCard kit={kit} compact />
                    </Grid>
                  ))}
                  {!programKits.length && !loading && (
                    <Grid item xs={12}>
                      <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                        Cargando biblioteca de media kits…
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default MediaKitsTeaser;
